import { Request } from 'api';
import { AxiosResponse } from 'axios';
import { UpdateOrderParams, Order } from './orders.types';

interface PaginatedApiResponse {
    data: Array<Order>;
    links: any; // Replace 'any' with the actual type of 'links'
    meta: any; // Replace 'any' with the actual type of 'meta'
}

type ReturnTypeCollection = Promise<AxiosResponse<PaginatedApiResponse>>;

interface GetOrdersParams {
    order_status?:
        | 'all'
        | ''
        | 'received'
        | 'processing'
        | 'shipping'
        | 'completed'
        | 'cancelled'
        | 'backfilled'
        | 'archived';
    order_type?:
        | 'all'
        | ''
        | 'physical_product'
        | 'subscription'
        | 'comp'
        | 'replacement'
        | 'pod_transfer'
        | 'invoice';
    seller_org_uuid?: string;
    buyer_org_uuid?: string;
    athlete_uuid?: string;
    sort?: 'created_at' | '-created_at';
    page?: number;
    per_page?: number;
}

interface OrderLineItem {
    physical_product_uuid: string;
    quantity: number;
    amount: number;
    tax: number;
}

export interface Fp1SerialAssignment {
    physical_product_uuid: string;
    serial_number: string;
}

interface CreateOrderParams {
    seller_org_uuid: string;
    buyer_org_uuid?: string;
    athlete_uuid?: string;
    tax: number;
    shipping: number;
    shipping_address_1: string;
    shipping_address_2?: string;
    shipping_city: string;
    shipping_state: string;
    shipping_zip: string;
    order_type:
        | 'physical_product'
        | 'subscription'
        | 'comp'
        | 'replacement'
        | 'pod_transfer'
        | 'invoice';
    order_notes: string;
    product_line_items: OrderLineItem[];
    notes?: string;
    /** When line items include configured FP1 catalog products, one serial per unit. */
    fp1_serial_assignments?: Fp1SerialAssignment[];
    /** For invoice orders: the contract/invoice UUID this order is linked to. */
    invoice_contract_uuid?: string;
    /** For invoice orders: the human-readable invoice number (contract name). */
    invoice_number?: string;
}

const getOrders = (params?: GetOrdersParams): ReturnTypeCollection => {
    if (params?.order_type === 'all') {
        params.order_type = '';
    }

    if (params?.order_status === 'all') {
        params.order_status = '';
    }

    const filterParams = {
        'filter[order_status]': params?.order_status,
        'filter[order_type]': params?.order_type,
        'filter[seller_org_uuid]': params?.seller_org_uuid,
        'filter[buyer_org_uuid]': params?.buyer_org_uuid,
        'filter[athlete_uuid]': params?.athlete_uuid,
        sort: params?.sort,
        page: params?.page,
        per_page: params?.per_page,
    };
    return Request.get(`admin/marketplace/orders`, { params: filterParams });
};

const createOrder = (orderData: CreateOrderParams): ReturnTypeSingle => {
    return Request.post('admin/marketplace/orders', orderData);
};

type ReturnTypeSingle = Promise<AxiosResponse<Order>>;

const updateOrder = (orderUuid: string, orderData: UpdateOrderParams): ReturnTypeSingle => {
    return Request.put(`admin/marketplace/orders/${orderUuid}`, orderData);
};

type ReturnTypeOrderStatuses = Promise<AxiosResponse<Array<string>>>;

const getOrderStatuses = (): ReturnTypeOrderStatuses => {
    return Request.get(`admin/marketplace/orders/order-statuses`);
};

export interface PodAssignmentSlot {
    slot_index: number;
    order_line_item_uuid: string;
    device_uuid: string | null;
    serial_number: string | null;
}

export interface OldPodAssignmentInfo {
    device_uuid: string;
    serial_number: string | null;
    custody_status: string | null;
    org_name: string | null;
    athlete_name: string | null;
}

export interface OrderPodAssignmentsState {
    required_pod_count: number;
    assigned_count: number;
    remaining_slots: number;
    pod_serials_assigned: boolean;
    fp1_pod_ship_firmware_version?: string | null;
    slots: PodAssignmentSlot[];
    unavailable_device_uuids: string[];
    old_pod: OldPodAssignmentInfo | null;
}

const getOrderPodAssignments = (
    orderUuid: string,
): Promise<AxiosResponse<{ data: OrderPodAssignmentsState }>> => {
    return Request.get(`admin/marketplace/orders/${orderUuid}/pod-assignments`);
};

const saveOrderPodAssignments = (
    orderUuid: string,
    deviceUuids: string[],
    options?: { firmware_version?: string | null; old_pod_serial_number?: string | null },
): Promise<
    AxiosResponse<{
        data: { pod_assignments: OrderPodAssignmentsState; order: Order };
    }>
> => {
    return Request.post(`admin/marketplace/orders/${orderUuid}/pod-assignments`, {
        device_uuids: deviceUuids,
        firmware_version: options?.firmware_version ?? null,
        ...(options && 'old_pod_serial_number' in options
            ? { old_pod_serial_number: options.old_pod_serial_number ?? null }
            : {}),
    });
};

const clearOrderPodAssignments = (
    orderUuid: string,
): Promise<
    AxiosResponse<{
        data: { pod_assignments: OrderPodAssignmentsState; order: Order };
    }>
> => {
    return Request.delete(`admin/marketplace/orders/${orderUuid}/pod-assignments`);
};

export interface TrackingEvent {
    status: string | null;
    status_details: string | null;
    location: {
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        country?: string | null;
    } | null;
    status_date: string | null;
}

export interface OrderTrackingEvidence {
    captured_at: string;
    order: {
        uuid: string;
        created_at: string | null;
        order_status: string | null;
        order_type: string | null;
        order_notes: string | null;
        total_cents: number;
        tax_cents: number;
        shipping_cents: number;
        shipping_tracking_number: string | null;
        delivered_at: string | null;
        shipping_address: {
            line1: string;
            line2: string | null;
            city: string;
            state: string;
            zip: string;
        };
        line_items: Array<{
            description: string;
            quantity: number;
            unit_amount_cents: number;
            amount_subtotal_cents: number;
        }>;
        seller_org_name: string | null;
        buyer_org_name: string | null;
        buyer_athlete_name: string | null;
        buyer_athlete_email: string | null;
    };
    tracking: {
        carrier: string;
        tracking_number: string;
        current_status: string | null;
        current_status_details: string | null;
        current_location: TrackingEvent['location'];
        status_date: string | null;
        eta: string | null;
        is_delivered: boolean;
        delivery_date: string | null;
        events: TrackingEvent[];
    };
}

const getOrderTrackingEvidence = (
    orderUuid: string,
): Promise<AxiosResponse<{ data: OrderTrackingEvidence }>> => {
    return Request.get(`admin/marketplace/orders/${orderUuid}/tracking`);
};

const getOrderReturnTrackingEvidence = (
    orderUuid: string,
): Promise<AxiosResponse<{ data: OrderTrackingEvidence }>> => {
    return Request.get(`admin/marketplace/orders/${orderUuid}/return-tracking`);
};

export {
    updateOrder,
    getOrders,
    getOrderStatuses,
    createOrder,
    getOrderPodAssignments,
    saveOrderPodAssignments,
    clearOrderPodAssignments,
    getOrderTrackingEvidence,
    getOrderReturnTrackingEvidence,
};
export type { UpdateOrderParams, GetOrdersParams, CreateOrderParams, OrderLineItem };
